<template>
  <validation-observer
    v-if="shouldQuestionRender && isRenderAfterFullfilled"
    v-slot="additionalDriversObserver"
    slim
  >
    <form-section
      id="additional-drivers"
      :validated="formObserver.validated"
      v-bind="additionalDriversObserver"
    >
      <form-row
        :label="title"
        :help-text="helpText"
        :error="formObserver.validated && !additionalDriversObserver.valid"
      >
        <radio
          v-model="hasAdditionalDrivers"
          :name="`${stepId}_additional-drivers`"
          data-test="additional_drivers"
          :items="constants.YES_NO"
        />
        <validation-provider
          v-slot="{ invalid, errors }"
          :vid="`${stepId}_additional-drivers-type-of-use`"
          name="Additional Drivers"
          :rules="{
            additionalDrivers: {
              typeOfUse: vehicleTypeOfUse,
              vehicleTypeOfUseRequiredDrivers: constants.VEHICLE_TYPE_OF_USE_REQUIRED_DRIVERS,
              productName,
            },
          }"
          immediate
        >
          <input
            type="hidden"
            :value="additionalDrivers.length"
          >
          <div
            v-if="invalid"
            class="mt-4 fz12"
            :class="{
              'error--text': formObserver.validated,
            }"
          >
            {{ errors[0] }}
          </div>
        </validation-provider>
      </form-row>

      <template v-if="hasAdditionalDrivers">
        <form-row v-if="additionalDrivers.length">
          <validation-provider
            v-slot="{ invalid }"
            :vid="`${stepId}_additional-drivers-relationship-to-proposer`"
            :rules="{ is: { other: true } }"
            name="Additional Relationship"
            immediate
          >
            <input
              type="hidden"
              :value="isRelationshipProposerValid && isAllAdditionalDriversValid"
            >
            <div
              v-if="invalid"
              class="error--text mb-4 fz12"
            >
              This doesn't look quite right to us, based on what you've told us so far. Please fix all issues marked red.
            </div>
          </validation-provider>

          <box
            v-for="(additionalDriver, index) in additionalDrivers"
            :key="index"
            :class="{
              'mt-4': index > 0,
            }"
          >
            <icon
              slot="icon"
              size="56"
              :icon="helpers.getAdditionalIcon(additionalDriver.title)"
            />
            <ul class="mb-6">
              <li>
                <p><strong>{{ additionalDriver.title }} {{ additionalDriver.firstName }} {{ additionalDriver.surname }}</strong></p>
              </li>
              <li>
                <p>{{ yearsToToday(additionalDriver.dateOfBirth) }} years old</p>
              </li>
              <li>
                <p>{{ additionalDriver.dateOfBirth }}</p>
              </li>
              <li>
                <p>
                  {{ constants.DRIVER_MARITAL_STATUS[additionalDriver.maritalStatus] }},
                  <span
                    :class="{
                      'error--text': !validateDriverRelationship(additionalDriver.relationshipProposer, index),
                    }"
                  >
                    {{ constants.ADDITIONAL_RELATIONSHIP_PROPOSER[additionalDriver.relationshipProposer] }}
                  </span>
                </p>
              </li>
              <validation-provider
                v-slot="{ invalid }"
                :vid="`${stepId}_additional-drivers-validity`"
                :rules="{ is: { other: true } }"
                name="Additional Drivers Validity"
                immediate
              >
                <input
                  type="hidden"
                  :value="getDriverValidity(index)"
                >
                <li
                  v-if="invalid"
                  class="error--text"
                >
                  There are some questions that you haven't answered
                </li>
              </validation-provider>
            </ul>
            <a
              slot="left-action"
              href="#"
              class="link-box"
              data-test="remove_driver_btn"
              @click.prevent="$emit('delete-driver', index)"
            >
              <span>Remove</span>
              <v-icon color="primary">mdi-delete</v-icon>
            </a>

            <a
              slot="right-action"
              href="#"
              class="link-box"
              data-test="change_driver_btn"
              @click.prevent="$emit('change-driver', index + 1)"
            >
              <span>
                <template v-if="validateDriverRelationship(additionalDriver.relationshipProposer, index)">Change</template>
                <template v-else>Fix</template>
                these details
              </span>
              <v-icon color="primary">mdi-pencil</v-icon>
            </a>
          </box>
        </form-row>
        <validation-provider
          v-slot="{ failed }"
          :vid="`${stepId}_has-additional-drivers`"
          name="Additional Drivers"
          :rules="{ minValue: 1 }"
        >
          <input
            type="hidden"
            :value="additionalDrivers.length"
          >
          <form-row v-if="failed && formObserver.validated">
            <div class="error--text mt-4 fz12">
              {{ additionalDriversFailedText }}
            </div>
          </form-row>
        </validation-provider>
        <form-row>
          <qz-btn
            v-if="formData.drivers.length < constants.DRIVERS_MAX_QTY"
            color="navy-light"
            rounded
            x-large
            dark
            data-test="add_driver_btn"
            @click.prevent="$emit('add-driver')"
          >
            Add another {{ constants.VEHICLE_DRIVER_TYPE }}
            <v-icon
              dark
              right
              size="22"
            >
              mdi-plus
            </v-icon>
          </qz-btn>
          <p v-else>You can add up to {{ constants.DRIVERS_MAX_QTY - 1 }} additional drivers.</p>
        </form-row>
      </template>
    </form-section>
  </validation-observer>
</template>

<script>
import pluralize from 'pluralize';
import { ExtDate } from '@/helpers/date';

import QuestionBase from '@/components/questions/Base';

export default {
  name: 'question-additional-drivers',
  extends: QuestionBase,
  props: {
    formObserver: {
      type: Object,
      required: true,
    },
    formData: {
      type: Object,
      required: true,
    },
    showConfirmModal: {
      type: Function,
      required: true,
    },
    driversBackup: {
      type: Array,
      default: null,
    },
    additionalDriverStateValidity: {
      type: Array,
      default: null,
    },
  },
  inject: [
    'constants',
    'productName',
    'helpers',
  ],
  data () {
    return {
      hasAdditionalDrivers: this.formData.drivers.length > 1,
    };
  },
  computed: {
    additionalDriversFailedText () {
      return `Please add additional ${this.constants.VEHICLE_DRIVER_TYPE}s or select "No".`;
    },
    mainDriver () {
      return this.formData.drivers[0];
    },
    isRelationshipProposerValid () {
      return !this.additionalDrivers.some(({ relationshipProposer }, index) => {
        return !this.validateDriverRelationship(relationshipProposer, index);
      });
    },
    isAllAdditionalDriversValid () {
      return !this.additionalDriverStateValidity?.some(({ isValid }) => isValid === false);
    },
    additionalDrivers () {
      return this.hasAdditionalDrivers ? this.formData.drivers.filter((d, i) => i > 0) : [];
    },
    vehicleTypeOfUse () {
      if (this.productName === 'bike') {
        return this.stepData.typeOfUse;
      }
      return this.formData.vehicle.typeOfUse;
    },
  },
  watch: {
    hasAdditionalDrivers (val) {
      if (val) {
        const hasBackup = !!this.driversBackup && this.driversBackup.length;
        if (hasBackup) {
          this.$emit('restore-additional-drivers', this.driversBackup);
          this.$emit('backup-additional-drivers', null);
          this.$emit('additional-drivers-status-check', true);
        }
      } else if (this.formData.drivers.length > 1) {
        this.showConfirmModal({
          text: `Clicking Yes will remove additional ${pluralize('driver', this.formData.drivers.length - 1)}.`,
          yesButton: 'Yes',
          noButton: 'No',
          yesCallback: () => {
            this.$emit('backup-additional-drivers', this.formData.drivers.slice(1));
            this.$emit('remove-additional-drivers');
            this.stepData.mainUserOfVehicle = 1;
          },
          noCallback: () => {
            this.hasAdditionalDrivers = true;
            this.$emit('additional-drivers-status-check', true);
          },
        });
      }
    },
    'formData.drivers.length' (val) {
      if (val === 1) {
        this.$emit('additional-drivers-status-check', false);
        this.hasAdditionalDrivers = false;
      }
    },
  },
  methods: {
    yearsToToday (date) {
      return new ExtDate().differenceInYears(new ExtDate(date));
    },
    validateDriverRelationship (value, driverIndex) {
      const additionalDriverMaritalStatus = this.additionalDrivers[driverIndex].maritalStatus;

      // Can't be Spouse or Civil Partner if main driver is not married and not in civil partnership
      if (!['M', 'V'].includes(this.mainDriver.maritalStatus) && ['S', 'V'].includes(value)) {
        return false;
      }

      // Can't be Common Law Spouse if main driver is not Common Law
      if ((!['C'].includes(this.mainDriver.maritalStatus) || !['C'].includes(additionalDriverMaritalStatus)) && ['C'].includes(value)) {
        return false;
      }

      return true;
    },
    getDriverValidity (index) {
      return this.additionalDriverStateValidity?.[index]?.isValid;
    },
  },
};
</script>
